type Gtag = (...args: any[]) => void

// Remembers what was pushed to which dataLayer, so a push dropped before GA
// initialization (gtag is a no-op without window.dataLayer) is retried later.
let applied: { dataLayer: unknown, userId: string | null } | undefined

function currentDataLayer() {
  return (window as { dataLayer?: unknown[] }).dataLayer
}

function analyticsUserIdState() {
  return useState<string | null>('analytics-user-id', () => null)
}

/**
 * Keeps the pseudonymous GA4 User-ID in memory only and returns the current-user
 * response without it, so it never reaches the persisted auth store.
 */
export function takeAnalyticsUserId<T extends { analyticsUserId?: string }>(
  response: T | null | undefined,
): Omit<T, 'analyticsUserId'> | undefined {
  analyticsUserIdState().value = response?.analyticsUserId ?? null
  if (!response) {
    return undefined
  }
  const { analyticsUserId: _analyticsUserId, ...context } = response
  return context
}

/** Sets GA4's built-in user_id before an event; public report viewers never carry the advisor's ID. */
export function applyAnalyticsUserId(gtag: Gtag) {
  const route = useRouter().currentRoute.value
  const isPublicReport = route.path.startsWith('/web-report/') || 'reportAccessToken' in route.query
  const userId = isPublicReport ? null : analyticsUserIdState().value
  const dataLayer = currentDataLayer()
  const last = applied
  const current = last && last.dataLayer === dataLayer ? last.userId : null

  if (!dataLayer || current === userId) {
    return
  }
  gtag('set', { user_id: userId })
  applied = { dataLayer, userId }
}

export function clearAnalyticsUserId(gtag?: Gtag) {
  analyticsUserIdState().value = null
  const dataLayer = currentDataLayer()
  const last = applied
  if (gtag && dataLayer && last && last.dataLayer === dataLayer && last.userId) {
    gtag('set', { user_id: null })
    applied = { dataLayer, userId: null }
  }
}
